import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage } from 'react-intl';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import qs from 'qs';
import { connect } from 'react-redux';
import { withTracking } from 'react-tracker';
import GraphQLService from '../../service/GraphQLService';
import { SET_RUNTIME_VARIABLE, RUNTIME_LOADING_DATA } from '../../constants';
import history from '../../history';
import Callout from '../../components/Callout';
import s from './FormManager.css';
import { getTrackingEvent } from '../../tracking/events';

const messages = defineMessages({
  errorUpdatingData: {
    id: 'formManager.errorUpdatingData',
    defaultMessage: 'An error occurred while updating data',
  },
  errorGettingData: {
    id: 'formManager.errorGettingData',
    defaultMessage: 'An error occurred getting data',
  },
  formUpdated: {
    id: 'formManager.formUpdated',
    defaultMessage: 'Form successfully updated.',
  },
  errorOccurred: {
    id: 'formManager.errorOccurred',
    defaultMessage: 'An error occurred.',
  },
});

class FormManager extends Component {
  // GraphQL answers with HTTP 200 even when the mutation fails, so validation
  // failures arrive in `data.errors` rather than as a transport error.
  // `ValidationError` carries the per-field messages in `state` and leaves
  // `message` as a generic "The request is invalid."; every other error only
  // has `message`. Prefer `state` so the form can tell the user which rule
  // they broke instead of a bare "An error occurred."
  static collectErrorMessages(errors) {
    return (errors || []).reduce((collected, error) => {
      if (error.state) {
        Object.keys(error.state).forEach(key => {
          collected.push(...[].concat(error.state[key]));
        });
      } else if (error.message) {
        collected.push(error.message);
      }
      return collected;
    }, []);
  }

  static propTypes = {
    children: PropTypes.element.isRequired,
    title: PropTypes.string,
    onData: PropTypes.func,
    onChange: PropTypes.func,
    // eslint-disable-next-line react/forbid-prop-types
    value: PropTypes.object,
    isSubmitting: PropTypes.bool,
    isLoadingData: PropTypes.func.isRequired,
    getQueryString: PropTypes.func,
    queryContext: PropTypes.string,
    track: PropTypes.func.isRequired,
    addQueryFields: PropTypes.string,
    onValidSubmit: PropTypes.func,
    forwardedRef: PropTypes.oneOfType([
      PropTypes.func,
      // eslint-disable-next-line react/forbid-prop-types
      PropTypes.shape({ current: PropTypes.any }),
    ]),
  };

  static contextTypes = {
    routeParams: PropTypes.object.isRequired,
    query: PropTypes.object.isRequired,
    path: PropTypes.string.isRequired,
    fetch: PropTypes.func.isRequired,
  };

  static defaultProps = {
    title: null,
    onData: null,
    onChange: null,
    value: {},
    getQueryString: null,
    isSubmitting: false,
    queryContext: null,
    addQueryFields: null,
    onValidSubmit: null,
    forwardedRef: null,
  };

  constructor(props, context) {
    super(props, context);
    this.queryKeys = '';
    this.state = {
      value: props.value,
      id: '',
      disabled: false,
      isSubmitting: false,
      callout: null,
      preventReinitialize: false, // Prevent reinitialization of the form when the id is set
    };
    this.graphQLService = new GraphQLService();
    this.form = null;

    if (props.forwardedRef) {
      if (typeof props.forwardedRef === 'function') {
        props.forwardedRef(this);
      } else {
        // eslint-disable-next-line no-param-reassign
        props.forwardedRef.current = this;
      }
    }

    this.onValidSubmit = this.onValidSubmit.bind(this);
    this.onInvalidSubmit = this.onInvalidSubmit.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onInit = this.onInit.bind(this);
    this.updateData = this.updateData.bind(this);
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.forwardedRef &&
      this.props.forwardedRef !== prevProps.forwardedRef
    ) {
      this.props.forwardedRef(this);
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.value !== this.props.value) {
      this.setState({ value: nextProps.value });
    }
  }

  onSubmit(e) {}

  onInvalidSubmit(e) {}

  onValidSubmit(e) {
    if (this.props.onValidSubmit) {
      this.props.onValidSubmit(e);
      return;
    }
    this.setState({
      disabled: true,
      value: e.value,
      isSubmitting: true,
      callout: null,
    });
    try {
      this.postData(e.value);
    } catch (e2) {
      this.setState({
        callout: {
          type: 'alert',
          children: (
            <span>
              <FormattedMessage {...messages.errorUpdatingData} /> -{' '}
              {e2.message}
            </span>
          ),
        },
      });
    }
  }

  onChange(e) {
    if (this.props.onChange) {
      this.props.onChange(e);
    }
  }

  onInit(e) {
    const { getQueryString } = this.props;
    const parsedQuery = Object.assign({}, this.context.query);
    this.form = e.target;
    if (
      (this.state.preventReinitialize === false &&
        parsedQuery.id &&
        parsedQuery.id !== '') ||
      typeof getQueryString === 'function'
    ) {
      this.setState({ disabled: true, id: parsedQuery.id }, () => {
        try {
          this.getData();
        } catch (e2) {
          this.setState({
            callout: {
              type: 'alert',
              children: (
                <span>
                  <FormattedMessage {...messages.errorGettingData} /> -{' '}
                  {e2.message}
                </span>
              ),
            },
          });
        }
      });
    }
  }

  async getData() {
    this.queryKeys = this.graphQLService.buildQueryKeys(
      this.form.getQueryObject(),
    );
    const {
      getQueryString,
      isLoadingData,
      queryContext,
      addQueryFields,
    } = this.props;
    const { siteId } = this.context.routeParams;
    isLoadingData(true);

    let fieldsQuery = [];
    if (this.form.props.metaData.graphQLTypes) {
      let graphQLTypes = this.form.props.metaData.graphQLTypes;
      graphQLTypes = Array.isArray(graphQLTypes)
        ? graphQLTypes
        : graphQLTypes();
      fieldsQuery = graphQLTypes.map(
        graphQLType => ` ... on ${graphQLType} { ${this.queryKeys} } `,
      );
    } else {
      fieldsQuery.push(this.queryKeys);
    }

    const getQueryStringVal =
      typeof getQueryString === 'function'
        ? getQueryString(
            this.form.props.metaData.graphQLModule,
            fieldsQuery,
            this.state,
          )
        : `{
        item: ${this.form.props.metaData.graphQLModule} {
          details: findById (id:"${this.state.id}" ${
            queryContext ? `, queryContext:"${queryContext}"` : ''
          }) {
            ${fieldsQuery.join(' ')} ${addQueryFields || ''}
          }
        }
      }`;

    const resp = await this.context.fetch(`/${siteId}/graphql`, {
      method: 'post',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ query: `${getQueryStringVal}` }),
      credentials: 'include',
    });
    if (resp.status !== 200) throw new Error(resp.statusText);
    const data = await resp.json();
    if (!data.errors) {
      this.setState({ value: data.data.item.details, disabled: false });
      if (this.props.onData) {
        this.props.onData(data.data.item.details, 'query');
      }
    } else {
      this.setState({ disabled: false });
    }
    isLoadingData(false);
  }

  async postData(formObject) {
    this.queryKeys = this.graphQLService.buildQueryKeys(
      this.form.getQueryObject(),
    );
    const { isLoadingData, title, getQueryString, addQueryFields } = this.props;
    const { siteId } = this.context.routeParams;
    isLoadingData(true);

    let fieldsQuery = [];
    if (this.form.props.metaData.graphQLTypes) {
      let graphQLTypes = this.form.props.metaData.graphQLTypes;
      graphQLTypes = Array.isArray(graphQLTypes)
        ? graphQLTypes
        : graphQLTypes(formObject);
      fieldsQuery = graphQLTypes.map(
        graphQLType => ` ... on ${graphQLType} { ${this.queryKeys} } `,
      );
    } else {
      fieldsQuery.push(this.queryKeys);
    }

    const resp = await this.context.fetch(`/${siteId}/graphql`, {
      method: 'post',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query: `mutation {
          details: ${this.form.props.metaData.graphQLModule}Mutation
            (data: ${this.graphQLService.escapeInputArg(formObject)}) {
              ${fieldsQuery.join(' ')} ${addQueryFields || ''}
          }
        }`,
      }),
      credentials: 'include',
    });
    if (resp.status !== 200) throw new Error(resp.statusText);
    const data = await resp.json();

    if (!data.errors) {
      const parsedQuery = Object.assign({}, this.context.query);
      if (
        data.data.details._id &&
        (!parsedQuery.id || parsedQuery.id === '') &&
        typeof getQueryString !== 'function'
      ) {
        parsedQuery.id = data.data.details._id;
        history.replace(
          `${this.context.path}?${qs.stringify(parsedQuery, {
            encode: false,
          })}`,
        );
        this.props.track({
          type: 'FORM_MANAGER_CREATE',
          siteId,
          data: {
            _id: data.data.details._id,
            form: title,
            type: data.data.details.type,
          },
        });
      } else {
        this.props.track({
          type: 'FORM_MANAGER_UPDATE',
          siteId,
          data: {
            _id: data.data.details._id,
            form: title,
            type: data.data.details.type,
          },
        });
      }
      window.scrollTo(0, 0);
      const boxScroll = window.document.getElementsByClassName('box--scroll');
      if (boxScroll.length > 0) boxScroll[0].scrollTo(0, 0);

      const modalBodyScroll = window.document.getElementsByClassName(
        'modal-skp--scroll',
      );
      if (modalBodyScroll.length > 0) modalBodyScroll[0].scrollTo(0, 0);

      this.setState({
        callout: {
          type: 'success',
          children: <FormattedMessage {...messages.formUpdated} />,
        },
        value: data.data.details,
        disabled: false,
        isSubmitting: false,
      });
      if (this.props.onData) {
        this.props.onData(data.data.details, 'post');
      }
    } else {
      const errorMessages = FormManager.collectErrorMessages(data.errors);
      this.setState({
        disabled: false,
        isSubmitting: false,
        callout: {
          type: 'alert',
          children:
            errorMessages.length > 0 ? (
              <span>
                <FormattedMessage {...messages.errorOccurred} />{' '}
                {errorMessages.join(' ')}
              </span>
            ) : (
              <FormattedMessage {...messages.errorOccurred} />
            ),
        },
      });
    }
    isLoadingData(false);
  }

  updateData({ data = null, preventReinitialize = false }) {
    console.info('updateData called', data);
    const newState = {};
    if (data) {
      newState.value = data;
    }
    if (preventReinitialize) {
      newState.preventReinitialize = preventReinitialize;
    }
    this.setState(newState, () => {
      if (preventReinitialize) this.setState({ preventReinitialize: false });
    });
    if (this.props.onData) {
      this.props.onData(data, 'updateData');
    }
  }

  render() {
    const titlePrefix = this.state.id === '' ? 'Add' : 'Edit';

    const childrenWithProps = React.Children.map(this.props.children, child =>
      React.cloneElement(child, {
        title: this.props.title,
        onInvalidSubmit: this.onInvalidSubmit,
        onValidSubmit: this.onValidSubmit,
        onSubmit: this.onSubmit,
        onChange: this.onChange,
        onInit: this.onInit,
        value: this.state.value,
        isSubmitting: this.state.isSubmitting || this.props.isSubmitting,
        submitButtonLabel:
          this.state.isSubmitting || this.props.isSubmitting
            ? '...submitting'
            : 'Submit',
      }),
    );

    return (
      <div className={`${s.wrapper} form-m`}>
        <div className="form-m__header">
          {this.props.title && (
            <h3 className="title title--secondary">{`${titlePrefix} ${this.props.title}`}</h3>
          )}
          {this.state.callout ? (
            <Callout size="" {...this.state.callout} />
          ) : null}
        </div>
        {childrenWithProps}
      </div>
    );
  }
}

const mapDispatchToProps = dispatch => ({
  isLoadingData: value =>
    dispatch({
      type: SET_RUNTIME_VARIABLE,
      payload: { name: RUNTIME_LOADING_DATA, value },
    }),
});

const mapTrackingToProps = trackEvent => ({
  track: ({ type, siteId, data }) => {
    trackEvent(getTrackingEvent({ type, siteId, data }));
  },
});

const StyledFormManager = withStyles(s)(FormManager);
const ConnectedFormManager = connect(
  null,
  mapDispatchToProps,
)(StyledFormManager);
const TrackedFormManager = withTracking(mapTrackingToProps)(
  ConnectedFormManager,
);

const ForwardedFormManager = React.forwardRef((props, ref) => (
  <TrackedFormManager {...props} forwardedRef={ref} />
));

export default ForwardedFormManager;



// WEBPACK FOOTER //
// ./src/components/FormManager/FormManager.js